/**
 * ============================================================================
 * NOTIFICATION SERVICE v1.0.2 - TOAST FEEDBACK METHODS
 * ============================================================================
 * Verwaltet Browser Notifications für Kalender-Erinnerungen
 *
 * Features:
 * - Browser Push Notifications
 * - Automatische Reminder-Checks
 * - Permission Management
 * - LocalStorage Persistence
 * - ✅ v1.0.2: Toast-style success/warning/error Methods
 *
 * @version 1.0.2 - Toast Feedback Methods
 */

class NotificationService {
  
  constructor() {
    this.checkInterval = null;
    this.lastCheck = null;
  }

  /**
   * Request Browser Notification Permission
   */
  async requestPermission() {
    console.log('🔔 Requesting notification permission...');
    
    if (!('Notification' in window)) {
      console.warn('⚠️ Browser unterstützt keine Notifications');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      console.log('✅ Permission already granted');
      return true;
    }
    
    if (Notification.permission === 'denied') {
      console.warn('❌ Permission denied by user');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      console.log('🔔 Permission result:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('❌ Error requesting permission:', error);
      return false;
    }
  }

  /**
   * Check Permission Status
   */
  hasPermission() {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  /**
   * Check all appointments for upcoming reminders
   */
  async checkReminders(appointments) {
    if (!this.hasPermission()) {
      console.log('🔔 No notification permission');
      return;
    }
    
    const now = new Date();
    
    // Verhindere zu häufige Checks
    if (this.lastCheck && (now - this.lastCheck) < 30000) { // 30 Sekunden
      return;
    }
    
    this.lastCheck = now;
    
    console.log('🔔 Checking reminders for', appointments.length, 'appointments');
    
    const shownReminders = this.getShownReminders();
    
    appointments.forEach(apt => {
      if (!apt.reminder_minutes) return;
      
      // Skip if already shown
      const reminderId = `${apt.id}-${apt.date}`;
      if (shownReminders.includes(reminderId)) {
        return;
      }
      
      try {
        // Parse Termin-Zeit
        const [hours, minutes] = apt.time.split(':');
        const aptDateTime = new Date(apt.date);
        aptDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        // Berechne Reminder-Zeit
        const reminderTime = new Date(aptDateTime);
        reminderTime.setMinutes(reminderTime.getMinutes() - apt.reminder_minutes);
        
        // Prüfe ob Reminder fällig ist
        const timeDiff = reminderTime - now;
        const minutesUntilReminder = timeDiff / (1000 * 60);
        
        console.log(`🔔 ${apt.title}:`, {
          reminderTime: reminderTime.toLocaleString(),
          minutesUntil: Math.round(minutesUntilReminder),
          shouldShow: minutesUntilReminder >= -1 && minutesUntilReminder <= 5
        });
        
        // Zeige Notification wenn innerhalb der nächsten 5 Minuten
        // oder bis 1 Minute nach Reminder-Zeit (falls App gerade geöffnet wurde)
        if (minutesUntilReminder >= -1 && minutesUntilReminder <= 5) {
          this.showNotification(apt, apt.reminder_minutes);
          this.markReminderAsShown(reminderId);
        }
      } catch (error) {
        console.error('❌ Error checking reminder for', apt.title, error);
      }
    });
  }

  /**
   * Show Browser Notification
   */
  showNotification(appointment, minutesBefore) {
    if (!this.hasPermission()) return;
    
    const title = `📅 Erinnerung: ${appointment.title}`;
    
    let body = '';
    if (minutesBefore <= 0) {
      body = `Jetzt um ${appointment.time} Uhr`;
    } else if (minutesBefore < 60) {
      body = `In ${minutesBefore} Minuten um ${appointment.time} Uhr`;
    } else {
      const hours = Math.floor(minutesBefore / 60);
      body = `In ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'} um ${appointment.time} Uhr`;
    }
    
    if (appointment.location) {
      body += `\n📍 ${appointment.location}`;
    }
    
    console.log('🔔 Showing notification:', title);
    
    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `apt-${appointment.id}`,
        requireInteraction: false,
        silent: false,
        data: {
          appointmentId: appointment.id,
          url: window.location.origin + '/calendar'
        }
      });
      
      notification.onclick = () => {
        console.log('🔔 Notification clicked');
        window.focus();
        notification.close();
      };
      
      // Auto-close nach 10 Sekunden
      setTimeout(() => {
        notification.close();
      }, 10000);
      
    } catch (error) {
      console.error('❌ Error showing notification:', error);
    }
  }

  /**
   * Schedule Reminder für späteren Zeitpunkt
   */
  async scheduleReminder(appointment) {
    if (!this.hasPermission()) {
      const granted = await this.requestPermission();
      if (!granted) {
        console.log('❌ Cannot schedule reminder without permission');
        return false;
      }
    }
    
    console.log('⏰ Reminder scheduled for:', appointment.title, 
                'at', appointment.date, appointment.time,
                '-', appointment.reminder_minutes, 'minutes before');
    
    return true;
  }

  /**
   * Get shown reminders from localStorage
   */
  getShownReminders() {
    try {
      const stored = localStorage.getItem('shown_reminders');
      if (!stored) return [];
      
      const reminders = JSON.parse(stored);
      
      // Cleanup alte Reminders (älter als 1 Tag)
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      const filtered = reminders.filter(r => {
        const date = r.split('-').slice(1).join('-'); // Extract date from "id-YYYY-MM-DD"
        return new Date(date) >= oneDayAgo;
      });
      
      if (filtered.length !== reminders.length) {
        localStorage.setItem('shown_reminders', JSON.stringify(filtered));
      }
      
      return filtered;
    } catch (error) {
      console.error('❌ Error reading shown reminders:', error);
      return [];
    }
  }

  /**
   * Mark reminder as shown
   */
  markReminderAsShown(reminderId) {
    try {
      const shown = this.getShownReminders();
      if (!shown.includes(reminderId)) {
        shown.push(reminderId);
        localStorage.setItem('shown_reminders', JSON.stringify(shown));
        console.log('✅ Marked reminder as shown:', reminderId);
      }
    } catch (error) {
      console.error('❌ Error marking reminder as shown:', error);
    }
  }

  /**
   * Start automatic reminder checking
   */
  startReminderChecking(appointments, intervalMs = 60000) {
    console.log('🔔 Starting reminder checking...');
    
    // Clear existing interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    // Initial check
    this.checkReminders(appointments);
    
    // Setup interval
    this.checkInterval = setInterval(() => {
      this.checkReminders(appointments);
    }, intervalMs);
  }

  /**
   * Stop automatic reminder checking
   */
  stopReminderChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('🔔 Stopped reminder checking');
    }
  }

  /**
   * Test notification (for debugging)
   */
  async testNotification() {
    if (!this.hasPermission()) {
      const granted = await this.requestPermission();
      if (!granted) return;
    }

    new Notification('PLANIVIO Test Notification', {
      body: 'Notifications funktionieren! 🎉',
      icon: '/planivio_icon_192px.png',
      tag: 'test'
    });
  }

  // ============================================================================
  // ✅ v1.0.2: TOAST-STYLE FEEDBACK METHODS
  // ============================================================================

  /**
   * Zeigt eine Erfolgs-Meldung (Toast)
   * @param {string} message - Die Nachricht
   */
  success(message) {
    console.log('✅ Success:', message);
    this._showToast(message, 'success');
  }

  /**
   * Zeigt eine Warnung (Toast)
   * @param {string} message - Die Nachricht
   */
  warning(message) {
    console.warn('⚠️ Warning:', message);
    this._showToast(message, 'warning');
  }

  /**
   * Zeigt einen Fehler (Toast)
   * @param {string} message - Die Nachricht
   */
  error(message) {
    console.error('❌ Error:', message);
    this._showToast(message, 'error');
  }

  /**
   * Interner Toast/Notification Helper
   * @private
   */
  _showToast(message, type = 'info') {
    let toastContainer = document.getElementById('notification-toast-container');

    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'notification-toast-container';
      toastContainer.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 8px;
        pointer-events: none;
      `;
      document.body.appendChild(toastContainer);
    }

    const toast = document.createElement('div');

    const colors = {
      success: { bg: '#059669', icon: '✅' },
      warning: { bg: '#D97706', icon: '⚠️' },
      error: { bg: '#DC2626', icon: '❌' },
      info: { bg: '#4A90A4', icon: 'ℹ️' }
    };

    const { bg, icon } = colors[type] || colors.info;

    toast.style.cssText = `
      background: ${bg};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex;
      align-items: center;
      gap: 8px;
      pointer-events: auto;
      animation: slideIn 0.3s ease;
      max-width: 90vw;
    `;

    toast.innerHTML = `<span>${icon}</span><span>${message}</span>`;

    toastContainer.appendChild(toast);

    // Animation CSS
    if (!document.getElementById('toast-animation-style')) {
      const style = document.createElement('style');
      style.id = 'toast-animation-style';
      style.textContent = `
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    // Auto-close nach 4 Sekunden
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        toast.remove();
        if (toastContainer.children.length === 0) {
          toastContainer.remove();
        }
      }, 300);
    }, 4000);
  }
}

// ✅ FIXED: Create instance first, then export
const notificationService = new NotificationService();
export default notificationService;